import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import GetAppIcon from '@mui/icons-material/GetApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';

const localizer = momentLocalizer(moment);

const Calendar = () => {
  const navigate = useNavigate();
  const { currentFamily } = useFamily();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    host: '',
    event_date: '',
    location: '',
    event_type: 'holiday',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEventForMenu, setSelectedEventForMenu] = useState(null);

  const fetchEvents = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/events/?family_id=${currentFamily.id}`);
      setEvents(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchEvents();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchEvents]);

  // Convert events to react-big-calendar format
  const calendarEvents = useMemo(() => {
    return events.map((event) => ({
      id: event.id,
      title: event.title,
      start: new Date(event.event_date),
      end: new Date(new Date(event.event_date).getTime() + 2 * 60 * 60 * 1000), // 2 hours duration
      resource: event, // Store full event data
    }));
  }, [events]);

  const canEditEvent = (event) => {
    if (!user || !currentFamily) return false;
    const isCreator = event.created_by === user.uid;
    const userMember = currentFamily?.members?.find((m) => m.user_id === user.uid);
    const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
    return isCreator || isAdmin;
  };

  const handleSelectEvent = (event) => {
    navigate(`/events/${event.resource.id}`);
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEventForMenu(event.resource);
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEventForMenu(null);
  };

  const handleEditClick = (event) => {
    handleMenuClose();
    const eventDate = new Date(event.event_date);
    const formattedDate = new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      host: event.host || '',
      event_date: formattedDate,
      location: event.location || '',
      event_type: event.event_type || 'holiday',
    });
    setSelectedEvent(event);
    setEditDialogOpen(true);
  };

  const handleUpdateEvent = async () => {
    try {
      await api.put(`/events/${selectedEvent.id}/`, editFormData);
      await fetchEvents();
      setEditDialogOpen(false);
      setSelectedEvent(null);
      alert('Event updated successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update event');
    }
  };

  const handleDeleteClick = (event) => {
    handleMenuClose();
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/events/${eventToDelete.id}/`);
      await fetchEvents();
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      alert('Event deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete event');
    }
  };

  // Export event to iCal format
  const exportToCalendar = (event) => {
    handleMenuClose();
    const startDate = new Date(event.event_date);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    
    const formatICalDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const description = [
      event.description || '',
      `Host: ${event.host}`,
      `Location: ${event.location}`,
    ].filter(Boolean).join('\\n');

    const icalContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Jubilea//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@jubilea`,
      `DTSTART:${formatICalDate(startDate)}`,
      `DTEND:${formatICalDate(endDate)}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${event.location}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Custom event component with edit/delete buttons
  const EventComponent = ({ event }) => {
    const canEdit = canEditEvent(event.resource);
    
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          cursor: 'pointer',
        }}
        onClick={(e) => handleEventClick(event, e)}
      >
        <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {event.title}
        </Typography>
        {canEdit && (
          <IconButton
            size="small"
            sx={{ p: 0.25, ml: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleEventClick(event, e);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  // Custom styles for the calendar
  const eventStyleGetter = (event) => {
    const eventType = event.resource?.event_type || 'holiday';
    const colors = {
      holiday: '#1976d2',
      birthday: '#d32f2f',
      anniversary: '#c2185b',
      wedding: '#7b1fa2',
      graduation: '#0288d1',
      party: '#f57c00',
      gathering: '#388e3c',
      other: '#616161',
    };
    
    return {
      style: {
        backgroundColor: colors[eventType] || colors.other,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.875rem',
      },
    };
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Holiday Calendar</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/events')}
        >
          Create Event
        </Button>
      </Box>

      {!currentFamily ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to view your calendar.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {currentFamily.google_calendar_embed_url && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Google Calendar</Typography>
                  <Box sx={{ height: 600, mt: 1 }}>
                    <iframe
                      src={currentFamily.google_calendar_embed_url}
                      style={{ border: 0, width: '100%', height: '100%' }}
                      frameBorder="0"
                      scrolling="no"
                      title="Google Calendar"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <CardContent>
                {currentFamily.google_calendar_embed_url && (
                  <Typography variant="h6" gutterBottom>Family Events</Typography>
                )}
                <Box sx={{ height: 600, mt: currentFamily.google_calendar_embed_url ? 1 : 2 }}>
                  <BigCalendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    onSelectEvent={handleSelectEvent}
                    eventPropGetter={eventStyleGetter}
                    components={{
                      event: EventComponent,
                    }}
                    views={['month']}
                    defaultView="month"
                    popup
                    showMultiDayTimes
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Event Menu Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {selectedEventForMenu && (
          <List dense>
            <ListItem>
              <ListItemText
                primary={selectedEventForMenu.title}
                secondary={new Date(selectedEventForMenu.event_date).toLocaleString()}
              />
            </ListItem>
            <ListItem button onClick={() => navigate(`/events/${selectedEventForMenu.id}`)}>
              <ListItemText primary="View Details" />
            </ListItem>
            {canEditEvent(selectedEventForMenu) && (
              <>
                <ListItem button onClick={() => handleEditClick(selectedEventForMenu)}>
                  <ListItemText primary="Edit" />
                  <ListItemSecondaryAction>
                    <EditIcon fontSize="small" />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem button onClick={() => handleDeleteClick(selectedEventForMenu)}>
                  <ListItemText primary="Delete" />
                  <ListItemSecondaryAction>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemSecondaryAction>
                </ListItem>
        </>
      )}
            <ListItem button onClick={() => exportToCalendar(selectedEventForMenu)}>
              <ListItemText primary="Add to Calendar" />
              <ListItemSecondaryAction>
                <GetAppIcon fontSize="small" />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        )}
      </Popover>

      {/* Edit Event Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            margin="normal"
            value={editFormData.title}
            onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={editFormData.description}
            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
          />
          <TextField
            fullWidth
            label="Host"
            margin="normal"
            value={editFormData.host}
            onChange={(e) => setEditFormData({ ...editFormData, host: e.target.value })}
          />
          <TextField
            fullWidth
            label="Event Date & Time"
            margin="normal"
            type="datetime-local"
            value={editFormData.event_date}
            onChange={(e) => setEditFormData({ ...editFormData, event_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Location"
            margin="normal"
            value={editFormData.location}
            onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event Type</InputLabel>
            <Select
              value={editFormData.event_type}
              onChange={(e) => setEditFormData({ ...editFormData, event_type: e.target.value })}
            >
              <MenuItem value="holiday">Holiday</MenuItem>
              <MenuItem value="birthday">Birthday</MenuItem>
              <MenuItem value="anniversary">Anniversary</MenuItem>
              <MenuItem value="wedding">Wedding</MenuItem>
              <MenuItem value="graduation">Graduation</MenuItem>
              <MenuItem value="party">Party</MenuItem>
              <MenuItem value="gathering">Gathering</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateEvent} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{eventToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Calendar;
